/**
 * Blog Content Index
 * Centralized blog post metadata for easy content management
 * Add new blog posts here to automatically include them in the blog index
 */

export interface BlogPost {
  title: string;
  slug: string;
  excerpt: string;
  category: string;
  readTime: string;
  date: string;
  author: string;
  featured?: boolean;
  tags: string[];
  featuredImage: string;
  relatedProducts?: string[];
}

export const blogPosts: BlogPost[] = [
  // COMPARISON & B2B POSTS
  {
    title: "REM7 vs Duravit SensoWash: Transparent Pricing vs Dealer Quotes",
    slug: "/blog/rem7-vs-duravit-sensowash",
    excerpt: "A like-for-like comparison of REM7 and Duravit SensoWash — feature parity, published pricing versus dealer quotes, and which suits your project.",
    category: "Comparisons",
    readTime: "12 min read",
    date: "August 17, 2026",
    author: "Aureum VII Trading",
    featured: true,
    tags: ["Duravit SensoWash", "Comparison", "Pricing", "B2B"],
    featuredImage: "/src/assets/blog/hygiene-luxury-bathroom.jpg",
    relatedProducts: ["rem7", "luxa", "aria"]
  },
  {
    title: "REM7 vs Kohler Veil (Premium Tier): A Luxury-to-Luxury Comparison",
    slug: "/blog/rem7-vs-kohler-veil",
    excerpt: "Comparing REM7 with Kohler Veil's premium wall-hung tier — finishes, EU market fit, installation support and design range.",
    category: "Comparisons",
    readTime: "12 min read",
    date: "August 17, 2026",
    author: "Aureum VII Trading",
    tags: ["Kohler Veil", "Comparison", "Wall-Hung", "Luxury"],
    featuredImage: "/src/assets/blog/what-is-smart-toilet-guide.jpg",
    relatedProducts: ["rem7", "aria", "aurelius"]
  },
  {
    title: "Smart Toilet Installation for Hotels: Retrofit, Maintenance & Lead Times",
    slug: "/blog/smart-toilet-installation-hotels",
    excerpt: "Plumbing and electrical retrofit planning, maintenance at scale, bulk-order lead times and accessibility for hospitality smart toilet rollouts.",
    category: "B2B & Projects",
    readTime: "13 min read",
    date: "August 17, 2026",
    author: "Aureum VII Trading",
    tags: ["Hospitality", "Installation", "B2B", "Retrofit"],
    featuredImage: "/src/assets/blog/hygiene-luxury-bathroom.jpg",
    relatedProducts: ["rem7", "aria", "luxa"]
  },
  {
    title: "Specifying Smart Toilets for Multi-Unit Residential Developments",
    slug: "/blog/specifying-smart-toilets-multi-unit-residential",
    excerpt: "Rough-in planning, electrical provision, water pressure requirements and procurement sequencing for apartment and villa developments.",
    category: "B2B & Projects",
    readTime: "12 min read",
    date: "August 19, 2026",
    author: "Aureum VII Trading",
    tags: ["Developers", "Specification", "Residential", "B2B"],
    featuredImage: "/src/assets/blog/what-is-smart-toilet-guide.jpg",
    relatedProducts: ["rem7", "aria", "luxa"]
  },
  {
    title: "Smart Toilet ROI for Boutique Hotels: A Developer's Guide",
    slug: "/blog/smart-toilet-roi-boutique-hotels",
    excerpt: "How to model the cost side of a smart toilet rollout honestly — capex, install, consumables saved and maintenance — without inflated payback claims.",
    category: "B2B & Projects",
    readTime: "11 min read",
    date: "August 19, 2026",
    author: "Aureum VII Trading",
    tags: ["Hospitality", "ROI", "B2B", "Procurement"],
    featuredImage: "/src/assets/blog/hygiene-luxury-bathroom.jpg",
    relatedProducts: ["rem7", "aria", "luxa"]
  },
  {
    title: "How to Write a Smart Toilet Spec Sheet for Your Next Project",
    slug: "/blog/smart-toilet-spec-sheet-guide",
    excerpt: "Every line item an architect should include when specifying REM7 products: dimensions, power, water pressure, clearances and rough-in detail.",
    category: "B2B & Projects",
    readTime: "12 min read",
    date: "August 19, 2026",
    author: "Aureum VII Trading",
    tags: ["Architects", "Spec Sheet", "Specification", "B2B"],
    featuredImage: "/src/assets/blog/what-is-smart-toilet-guide.jpg",
    relatedProducts: ["rem7", "aria", "luxa"]
  },
  {
    title: "Inodoro inteligente: guía para elegirlo en España",
    slug: "/blog/inodoro-inteligente-guia",
    excerpt: "Qué es un inodoro inteligente o japonés, qué instalación necesita en España y cómo especificarlo en viviendas, hoteles y promociones.",
    category: "Guides",
    readTime: "9 min",
    date: "October 8, 2026",
    author: "Aureum VII Trading",
    tags: ["Inodoro inteligente", "Inodoro japonés", "España", "B2B"],
    featuredImage: "/src/assets/blog/what-is-smart-toilet-guide.jpg",
    relatedProducts: ["rem7", "aria", "luxa"]
  },
  {
    title: "Japanese Toilet vs Luxury Smart Toilet: Installing One in Europe",
    slug: "/blog/japanese-toilet-vs-luxury-smart-toilet",
    excerpt: "Bidet seat or integrated smart toilet? What a 'Japanese toilet' really is and what European projects need for water, power and drainage.",
    category: "Guides",
    readTime: "8 min read",
    date: "October 8, 2026",
    author: "Aureum VII Trading",
    tags: ["Japanese toilet", "Smart toilets", "Europe", "B2B"],
    featuredImage: "/src/assets/blog/what-is-smart-toilet-guide.jpg",
    relatedProducts: ["rem7", "aria", "luna"]
  },
  // NEW HEALTH & WELLNESS POSTS
  {
    title: "Smart Toilets for Seniors: Independence, Safety & Comfort in Aging-in-Place",
    slug: "/blog/smart-toilets-for-seniors",
    excerpt: "How smart toilets enhance safety, independence, and comfort for elderly users with automatic lids, night lights, and bidet functions.",
    category: "Wellness",
    readTime: "11 min read",
    date: "January 20, 2025",
    author: "Aureum VII Trading",
    featured: true,
    tags: ["Senior Care", "Accessibility", "Safety", "Aging in Place"],
    featuredImage: "/src/assets/blog/what-is-smart-toilet-guide.jpg",
    relatedProducts: ["luna", "aria", "vena"]
  },
  {
    title: "Hemorrhoid Relief: How Smart Toilet Bidets Provide Comfort and Healing",
    slug: "/blog/hemorrhoid-relief-smart-toilets",
    excerpt: "Discover how smart toilet bidets provide gentle hemorrhoid relief through warm water therapy and friction-free hygiene.",
    category: "Wellness",
    readTime: "10 min read",
    date: "January 19, 2025",
    author: "Aureum VII Trading",
    tags: ["Hemorrhoid Relief", "Health Benefits", "Bidet Therapy"],
    featuredImage: "/src/assets/blog/hygiene-luxury-bathroom.jpg",
    relatedProducts: ["luxa", "luna", "nova"]
  },
  {
    title: "Postpartum Recovery: Essential Smart Toilet Features for New Mothers",
    slug: "/blog/postpartum-recovery-bathroom",
    excerpt: "How smart toilets support postpartum healing with gentle bidet cleansing and hands-free operation for new mothers.",
    category: "Wellness",
    readTime: "12 min read",
    date: "January 17, 2025",
    author: "Aureum VII Trading",
    tags: ["Postpartum", "New Mothers", "Recovery", "Maternal Health"],
    featuredImage: "/src/assets/blog/maternal-wellness-bathroom.jpg",
    relatedProducts: ["luna", "vena", "luxa"]
  },
  {
    title: "The Health Benefits of Bidet Toilets: What Science and Doctors Say",
    slug: "/blog/bidet-health-benefits",
    excerpt: "Explore the proven health benefits of bidet toilets: improved hygiene, reduced infections, skin health, and doctor recommendations.",
    category: "Wellness",
    readTime: "13 min read",
    date: "January 16, 2025",
    author: "Aureum VII Trading",
    tags: ["Health Benefits", "Hygiene", "Medical Recommendations"],
    featuredImage: "/src/assets/blog/hygiene-luxury-bathroom.jpg",
    relatedProducts: ["luxa", "nova", "luna"]
  },
  {
    title: "Smart Toilets for Accessibility: Independence for People with Disabilities",
    slug: "/blog/smart-toilets-accessibility",
    excerpt: "How smart toilets improve bathroom accessibility for people with disabilities with touchless controls and bidet functions.",
    category: "Wellness",
    readTime: "11 min read",
    date: "January 14, 2025",
    author: "Aureum VII Trading",
    tags: ["Accessibility", "Disability", "Independence", "Inclusive Design"],
    featuredImage: "/src/assets/blog/what-is-smart-toilet-guide.jpg",
    relatedProducts: ["aria", "luna", "vena"]
  },
  // NEW HEALTH & WELLNESS POSTS - BATCH 2
  {
    title: "Digestive Health and Smart Toilets: A Comprehensive Guide to Gut Wellness",
    slug: "/blog/digestive-health-smart-toilets",
    excerpt: "Discover how smart toilet technology supports digestive health and promotes better gut wellness through advanced bidet features.",
    category: "Wellness",
    readTime: "9 min read",
    date: "January 10, 2025",
    author: "Dr. Marcus Weber",
    tags: ["Digestive Health", "Gut Wellness", "IBS Relief", "Health Benefits"],
    featuredImage: "/src/assets/blog/digestive-health-smart-toilets.jpg",
    relatedProducts: ["luxa", "nova", "aria"]
  },
  {
    title: "UTI Prevention: How Bidet Toilets Support Urinary Health",
    slug: "/blog/uti-prevention-bidet",
    excerpt: "Learn how bidet toilets can help prevent urinary tract infections through improved hygiene and gentle cleansing techniques.",
    category: "Wellness",
    readTime: "8 min read",
    date: "January 12, 2025",
    author: "Dr. Sarah Mitchell",
    tags: ["UTI Prevention", "Women's Health", "Hygiene", "Urinary Health"],
    featuredImage: "/src/assets/blog/uti-prevention-bidet.jpg",
    relatedProducts: ["luxa", "luna", "aria"]
  },
  {
    title: "Eco-Friendly Smart Toilets: Sustainable Bathroom Solutions for 2025",
    slug: "/blog/eco-friendly-smart-toilet",
    excerpt: "Discover how smart toilets contribute to environmental sustainability through water conservation and reduced paper waste.",
    category: "Sustainability",
    readTime: "7 min read",
    date: "January 14, 2025",
    author: "Emma Richardson",
    tags: ["Sustainability", "Eco-Friendly", "Water Conservation", "Green Living"],
    featuredImage: "/src/assets/blog/eco-friendly-smart-toilet.jpg",
    relatedProducts: ["aria", "nova", "vena"]
  },
  {
    title: "Skin Health and Bidets: Dermatologist-Recommended Hygiene Solutions",
    slug: "/blog/skin-health-bidet",
    excerpt: "Discover why dermatologists recommend bidet toilets for sensitive skin care and how water cleansing protects skin health.",
    category: "Wellness",
    readTime: "7 min read",
    date: "January 16, 2025",
    author: "Dr. Elena Rodriguez",
    tags: ["Skin Health", "Dermatology", "Sensitive Skin", "Hygiene"],
    featuredImage: "/src/assets/blog/skin-health-bidet.jpg",
    relatedProducts: ["luxa", "aria", "luna"]
  },
  {
    title: "Nighttime Bathroom Safety: Smart Toilet Features That Prevent Falls",
    slug: "/blog/nighttime-bathroom-safety",
    excerpt: "Learn how smart toilet safety features including night lights and motion sensors help prevent falls during nighttime bathroom visits.",
    category: "Safety",
    readTime: "8 min read",
    date: "January 18, 2025",
    author: "James Morrison",
    tags: ["Safety", "Night Light", "Fall Prevention", "Senior Care"],
    featuredImage: "/src/assets/blog/nighttime-bathroom-safety.jpg",
    relatedProducts: ["luna", "nova", "u1"]
  },
  // EXISTING POSTS
  {
    title: "Square vs Egg Design: Choosing the Perfect Smart Toilet Shape",
    slug: "/blog/square-vs-egg-smart-toilet-design",
    excerpt: "Compare revolutionary square and award-winning egg designs. Learn which smart toilet shape best suits your bathroom aesthetics.",
    category: "Comparison",
    readTime: "12 min read",
    date: "January 18, 2025",
    author: "Aureum VII Trading",
    tags: ["smart toilets", "design comparison", "K2 Quadrate", "S1 Super Egg"],
    featuredImage: "/src/assets/k2-hero-desert.jpg",
    relatedProducts: ["k2", "s1"]
  },
  {
    title: "The Ultimate Guide to Luxury Smart Toilets in 2025",
    slug: "/blog/luxury-smart-toilet-guide",
    excerpt: "Complete guide to choosing, installing, and enjoying premium smart toilets. Features, costs, and expert recommendations.",
    category: "Buying Guide",
    readTime: "15 min read",
    date: "January 15, 2025",
    author: "Aureum VII Trading",
    tags: ["smart toilets", "buying guide", "luxury bathrooms"],
    featuredImage: "/src/assets/blog/hygiene-luxury-bathroom.jpg",
    relatedProducts: ["luxa", "nova", "aria"]
  },
  {
    title: "What is a Smart Toilet? Complete Guide to Intelligent Bathroom Technology",
    slug: "/blog/what-is-smart-toilet",
    excerpt: "Discover everything about smart toilets: features, benefits, costs, and how they work. Expert guide to bathroom innovation.",
    category: "Education",
    readTime: "12 min read",
    date: "January 10, 2025",
    author: "Aureum VII Trading",
    tags: ["smart toilets", "bathroom technology", "home automation"],
    featuredImage: "/src/assets/blog/what-is-smart-toilet-guide.jpg",
    relatedProducts: ["luxa", "luna"]
  },
  {
    title: "Smart Toilet Technology in 2025: Latest Innovations",
    slug: "/blog/smart-toilet-technology-2025",
    excerpt: "Explore the latest advancements in smart toilet technology, from AI-powered sensors to health monitoring features.",
    category: "Technology",
    readTime: "10 min read",
    date: "January 8, 2025",
    author: "Aureum VII Trading",
    tags: ["technology", "innovation", "AI"],
    featuredImage: "/src/assets/blog/european-smart-toilets-2025.jpg",
    relatedProducts: ["nova", "aurelux"]
  },
  {
    title: "European Smart Toilets 2025: Market Leader Comparison",
    slug: "/blog/european-smart-toilets-2025",
    excerpt: "Compare top European smart toilet brands and models. In-depth analysis of features, pricing, and performance.",
    category: "Comparison",
    readTime: "14 min read",
    date: "January 5, 2025",
    author: "Aureum VII Trading",
    tags: ["comparison", "European market", "buying guide"],
    featuredImage: "/src/assets/blog/european-smart-toilets-2025.jpg",
    relatedProducts: ["luxa", "nova", "aria", "luna"]
  },
  {
    title: "Bathroom Comfort for Women: Why Smart Toilets Make a Difference",
    slug: "/blog/women-bathroom-comfort",
    excerpt: "How premium smart toilets enhance comfort, hygiene, and wellness specifically for women's needs.",
    category: "Wellness",
    readTime: "11 min read",
    date: "January 3, 2025",
    author: "Aureum VII Trading",
    tags: ["women's health", "comfort", "wellness"],
    featuredImage: "/src/assets/blog/women-bathroom-comfort.jpg",
    relatedProducts: ["luna", "vena"]
  },
  {
    title: "Pregnancy Comfort: Smart Toilet Features for Expecting Mothers",
    slug: "/blog/pregnancy-smart-toilet-comfort",
    excerpt: "Essential smart toilet features that provide comfort, safety, and support during pregnancy.",
    category: "Pregnancy",
    readTime: "10 min read",
    date: "December 28, 2024",
    author: "Aureum VII Trading",
    tags: ["pregnancy", "maternal health", "comfort"],
    featuredImage: "/src/assets/blog/pregnancy-comfort-bathroom.jpg",
    relatedProducts: ["luna", "vena"]
  },
  {
    title: "Smart Toilet vs Traditional Bidet: Complete Comparison 2025",
    slug: "/blog/smart-toilet-vs-bidet",
    excerpt: "Detailed comparison of smart toilets and traditional bidets. Features, costs, installation, and which is right for you.",
    category: "Comparison",
    readTime: "13 min read",
    date: "December 25, 2024",
    author: "Aureum VII Trading",
    tags: ["comparison", "bidet", "buying guide"],
    featuredImage: "/src/assets/blog/bidet-toilet-comparison.jpg",
    relatedProducts: ["luxa", "aria"]
  },
  {
    title: "Water-Saving Smart Toilets: Eco-Luxury Bathroom Solutions",
    slug: "/blog/water-saving-smart-toilets",
    excerpt: "How modern smart toilets combine luxury with sustainability. Water-saving technology and environmental benefits.",
    category: "Sustainability",
    readTime: "9 min read",
    date: "December 20, 2024",
    author: "Aureum VII Trading",
    tags: ["sustainability", "water conservation", "eco-friendly"],
    featuredImage: "/src/assets/blog/mold-free-bathroom-design.jpg",
    relatedProducts: ["vena", "aria"]
  },
  {
    title: "LUNA M9 Review: Premium Wellness Smart Toilet Experience",
    slug: "/blog/luna-m9-review",
    excerpt: "In-depth review of the LUNA M9 smart toilet: features, performance, installation, and real-world experience.",
    category: "Product Review",
    readTime: "12 min read",
    date: "December 18, 2024",
    author: "Aureum VII Trading",
    tags: ["product review", "LUNA M9", "wellness"],
    featuredImage: "/src/assets/luna-m9-main-optimized.webp",
    relatedProducts: ["luna"]
  },
  {
    title: "Creating a Spa-Like Bathroom: Design Ideas with Smart Technology",
    slug: "/blog/spa-like-bathroom-design",
    excerpt: "Transform your bathroom into a luxury spa retreat with smart toilets, lighting, and wellness features.",
    category: "Design",
    readTime: "11 min read",
    date: "December 15, 2024",
    author: "Aureum VII Trading",
    tags: ["design", "spa bathroom", "luxury"],
    featuredImage: "/src/assets/blog/spa-bathroom-luxury.jpg",
    relatedProducts: ["nova", "aurelux", "luna"]
  }
];

// Helper function to get featured post
export const getFeaturedPost = () => blogPosts.find(post => post.featured);

// Helper function to get posts by category
export const getPostsByCategory = (category: string) => 
  blogPosts.filter(post => post.category === category);

// Helper function to get recent posts
export const getRecentPosts = (limit: number = 5) => 
  blogPosts.slice(0, limit);

// Categories for filtering
export const blogCategories = [
  "All",
  "Buying Guide",
  "Technology",
  "Wellness",
  "Sustainability",
  "Comparison",
  "Design",
  "Product Review",
  "Pregnancy",
  "Safety",
  "Education"
];
